// Fractional index keys, vendored from tldraw packages/utils/src/lib/{fractionalIndexing,reordering}.ts.
// tldraw's version trims the CC0-1.0 `fractional-indexing` package (https://observablehq.com/@dgreensp/implementing-fractional-indexing).
// Only the non-jittered keys are kept (decisions: deterministic index keys).
//
// MIT License
//
// Copyright (c) 2024 tldraw Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
// documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
// rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
// Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
// WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

/** Integer head + fraction (base 62, no trailing zero); keys order as plain strings. */
export type IndexKey = string & { __brand: 'indexKey' }

export const ZERO_INDEX_KEY = 'a0' as IndexKey

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const ZERO = '0'
const LARGEST_DIGIT = 'z'
/** The reserved smallest integer: it has no valid predecessor. */
const SMALLEST_INTEGER = `A${ZERO.repeat(26)}`

/** Every key is one or more characters of DIGITS (tldraw's validator leaves the alphabet unchecked). */
const KEY_ALPHABET = /^[0-9A-Za-z]+$/

/** Char code of a key character; keys are validated ASCII, so a missing character is a caller bug. */
function code(char: string): number {
  const c = char.codePointAt(0)
  if (c === undefined) throw new Error('missing order key digit')
  return c
}

function integerLength(head: string): number {
  if (head >= 'a' && head <= 'z') return code(head) - code('a') + 2
  if (head >= 'A' && head <= 'Z') return code('Z') - code(head) + 2
  throw new Error(`invalid order key head: ${head}`)
}

function integerPart(key: string): string {
  const length = integerLength(key.slice(0, 1))
  if (length > key.length) throw new Error(`invalid order key: ${key}`)
  return key.slice(0, length)
}

/** `DIGITS.indexOf` without the scan: DIGITS is in char-code order. */
function digitIndex(char: string): number {
  const c = code(char)
  if (c <= 57) return c - 48
  if (c <= 90) return c - 55
  return c - 61
}

function digit(index: number): string {
  if (index < 0 || index >= DIGITS.length) throw new Error(`order key digit ${index} out of range`)
  return DIGITS.charAt(index)
}

function validateOrderKey(key: string) {
  if (!KEY_ALPHABET.test(key) || key === SMALLEST_INTEGER) throw new Error(`invalid order key: ${key}`)
  const integer = integerPart(key)
  if (key.length > integer.length && key.at(-1) === ZERO) throw new Error(`invalid order key: ${key}`)
}

/** `a` may be empty, `b` is null or non-empty; `a < b`; no trailing zeros. */
function midpoint(a: string, b: string | null): string {
  if (b !== null && a >= b) throw new Error(`${a} >= ${b}`)
  if (a.at(-1) === ZERO || (b && b.at(-1) === ZERO)) throw new Error('trailing zero')
  if (b) {
    let n = 0
    while ((a[n] ?? ZERO) === b[n]) n++
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n))
  }
  const digitA = a ? digitIndex(a.charAt(0)) : 0
  const digitB = b === null ? DIGITS.length : digitIndex(b.charAt(0))
  if (digitB - digitA > 1) return digit(Math.round(0.5 * (digitA + digitB)))
  if (b && b.length > 1) return b.slice(0, 1)
  return digit(digitA) + midpoint(a.slice(1), null)
}

/** Null past the largest integer. */
function incrementInteger(x: string): string | null {
  const [head = '', ...digits] = x.split('')
  let carry = true
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    const d = digitIndex(digits[i]) + 1
    if (d === DIGITS.length) digits[i] = ZERO
    else {
      digits[i] = digit(d)
      carry = false
    }
  }
  if (!carry) return head + digits.join('')
  if (head === 'Z') return `a${ZERO}`
  if (head === 'z') return null
  const next = String.fromCodePoint(code(head) + 1)
  if (next > 'a') digits.push(ZERO)
  else digits.pop()
  return next + digits.join('')
}

/** Null below the smallest integer. */
function decrementInteger(x: string): string | null {
  const [head = '', ...digits] = x.split('')
  let borrow = true
  for (let i = digits.length - 1; borrow && i >= 0; i--) {
    const d = digitIndex(digits[i]) - 1
    if (d === -1) digits[i] = LARGEST_DIGIT
    else {
      digits[i] = digit(d)
      borrow = false
    }
  }
  if (!borrow) return head + digits.join('')
  if (head === 'a') return `Z${LARGEST_DIGIT}`
  if (head === 'A') return null
  const next = String.fromCodePoint(code(head) - 1)
  if (next < 'Z') digits.push(LARGEST_DIGIT)
  else digits.pop()
  return next + digits.join('')
}

/** A key strictly between `a` and `b` (null = open end); inputs already valid. */
function keyBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) throw new Error(`${a} >= ${b}`)
  if (a === null) {
    if (b === null) return `a${ZERO}`
    const ib = integerPart(b)
    const fb = b.slice(ib.length)
    if (ib === SMALLEST_INTEGER) return ib + midpoint('', fb)
    if (ib < b) return ib
    const decremented = decrementInteger(ib)
    if (decremented === null) throw new Error('cannot decrement any more')
    return decremented
  }
  if (b === null) {
    const ia = integerPart(a)
    const fa = a.slice(ia.length)
    const incremented = incrementInteger(ia)
    return incremented ?? ia + midpoint(fa, null)
  }
  const ia = integerPart(a)
  const fa = a.slice(ia.length)
  const ib = integerPart(b)
  const fb = b.slice(ib.length)
  if (ia === ib) return ia + midpoint(fa, fb)
  const incremented = incrementInteger(ia)
  if (incremented === null) throw new Error('cannot increment any more')
  if (incremented < b) return incremented
  return ia + midpoint(fa, null)
}

function nKeysBetween(a: string | null, b: string | null, n: number): string[] {
  if (n === 0) return []
  if (n === 1) return [keyBetween(a, b)]
  if (b === null) {
    let c = keyBetween(a, b)
    const result = [c]
    for (let i = 0; i < n - 1; i++) {
      c = keyBetween(c, b)
      result.push(c)
    }
    return result
  }
  if (a === null) {
    let c = keyBetween(a, b)
    const result = [c]
    for (let i = 0; i < n - 1; i++) {
      c = keyBetween(a, c)
      result.push(c)
    }
    return result.toReversed()
  }
  const mid = Math.floor(n / 2)
  const c = keyBetween(a, b)
  return [...nKeysBetween(a, c, mid), c, ...nKeysBetween(c, b, n - mid - 1)]
}

function generate(below: IndexKey | null | undefined, above: IndexKey | null | undefined, n: number): IndexKey[] {
  if (n === 0) return []
  const a = below ?? null
  const b = above ?? null
  if (a !== null) validateOrderKey(a)
  if (b !== null) validateOrderKey(b)
  return nKeysBetween(a, b, n) as IndexKey[]
}

export function validateIndexKey(index: string): asserts index is IndexKey {
  try {
    validateOrderKey(index)
  } catch {
    throw new Error(`invalid index: ${index}`)
  }
}

/** n ascending keys strictly inside (below, above); a missing bound is open. */
export function getIndicesBetween(below: IndexKey | null | undefined, above: IndexKey | null | undefined, n: number) {
  return generate(below, above, n)
}

export function getIndicesAbove(below: IndexKey | null | undefined, n: number) {
  return generate(below, null, n)
}

export function getIndicesBelow(above: IndexKey | null | undefined, n: number) {
  return generate(null, above, n)
}

export function getIndexBetween(below: IndexKey | null | undefined, above: IndexKey | null | undefined) {
  return generate(below, above, 1)[0]
}

export function getIndexAbove(below: IndexKey | null | undefined = null) {
  return generate(below, null, 1)[0]
}

export function getIndexBelow(above: IndexKey | null | undefined = null) {
  return generate(null, above, 1)[0]
}

/** `start` followed by n keys above it. */
export function getIndices(n: number, start = 'a1' as IndexKey) {
  return [start, ...generate(start, null, n)]
}

/**
 * Total order by (index, id): equal keys (concurrent non-jittered inserts) order by id, so the order is a function of
 * the record set (Excalidraw `fractionalIndex.ts:150-169`; tldraw returns 0 on ties).
 */
export function sortByIndex<T extends { index: IndexKey; id: string }>(a: T, b: T) {
  if (a.index !== b.index) return a.index < b.index ? -1 : 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}
